// Destino a retomar depois do login.
//
// O login do usuário comum é um modal (não uma rota), e o `storeToken` sempre
// mandava para a home. Quando o usuário chega por um link direto (ex.: o link de
// interesse no e-mail, ou a sessão que venceu no meio da navegação) guardamos
// aqui para onde ele queria ir, e o `storeToken` consome ao autenticar.
//
// Quem GRAVA (24/09/2026): o `disconnectUser` de Helpers/Error.js — que é por
// onde passam tanto a sessão expirada quanto o link aberto sem sessão —, o
// AdminLayout (admin sem token nenhum) e a tela /list-projects.
// Quem CONSOME: `storeToken(token, true)` em Contexts/Auth.js, usado por todas
// as portas de login.

const KEY = "postLoginRedirect";

// Depois disto o destino guardado é descartado. Sem prazo, quem abandona hoje
// seria jogado numa URL que não pediu no login da semana que vem. É a mesma
// ordem de grandeza dos links de senha/registro (SESSIONS.*_expiration_time).
const TTL_MS = 30 * 60 * 1000;

// Aceita SOMENTE caminho relativo da própria aplicação. Recusa URL absoluta e
// "//host" (que o browser trata como protocol-relative) para não virar um
// open redirect.
const isSafeRelativePath = (path) =>
  typeof path === "string" &&
  path.startsWith("/") &&
  !path.startsWith("//") &&
  !path.includes("://");

// Rotas que NÃO podem ser destino: guardar uma delas faria o login
// redirecionar para a própria tela de login, ou para a home de onde o usuário
// acabou de sair. Comparação por pathname, ignorando a query.
const NON_RESUMABLE = [
  "/",
  "/admin",
  "/admin/change-password",
  "/forgot-password",
];

const isResumable = (path) =>
  !NON_RESUMABLE.includes(path.split("?")[0].replace(/\/+$/, "") || "/");

export const setPostLoginRedirect = (path) => {
  if (!isSafeRelativePath(path) || !isResumable(path)) return false;

  localStorage.setItem(KEY, JSON.stringify({ path, at: Date.now() }));

  return true;
};

// Lê sem consumir (para decidir, por exemplo, se o modal de login deve abrir).
export const peekPostLoginRedirect = () => {
  const raw = localStorage.getItem(KEY);

  if (!raw) return null;

  let stored;

  // ⚠️ try/catch obrigatório: até 24/09/2026 a chave guardava a STRING CRUA, e
  // ainda pode haver um valor desses no navegador de quem já usou o sistema.
  // Um JSON.parse solto ali derrubaria o login.
  try {
    stored = JSON.parse(raw);
  } catch {
    // Formato legado: foi gravado antes desta entrega e não deve ressuscitar.
    localStorage.removeItem(KEY);

    return null;
  }

  if (!stored || typeof stored.at !== "number") {
    localStorage.removeItem(KEY);

    return null;
  }

  if (Date.now() - stored.at > TTL_MS) {
    localStorage.removeItem(KEY);

    return null;
  }

  return isSafeRelativePath(stored.path) ? stored.path : null;
};

// Lê, remove e devolve — usado no momento em que o redirecionamento acontece.
export const consumePostLoginRedirect = () => {
  const path = peekPostLoginRedirect();

  localStorage.removeItem(KEY);

  return path;
};

export const clearPostLoginRedirect = () => localStorage.removeItem(KEY);
